/**
 * 編集グリッド用の小さな入力（高さ 28px）。値が 0 の欄はフォーカス時に空欄になる（そのまま数字を入力できる）。表内で多数並ぶため数値・文字は共通の NumberField より軽量にしている。
 * 日付は共通の DateField（size="sm"）を使う（ブラウザ標準の日付入力は使わない）。
 */
import { useEffect, useState } from 'react';
import { DateField } from '@/components';
import { cx } from '@/lib/cx';
import { parseNumber } from '@/lib/format';

export function CellNumber({
  value,
  onChange,
  decimals = 0,
  kind = 'num',
  disabled,
  label,
  invalid,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  decimals?: number;
  kind?: 'num' | 'qty';
  disabled?: boolean;
  label: string;
  invalid?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(value == null ? '' : String(value));
  useEffect(() => {
    if (!focused) setText(value == null ? '' : String(value));
  }, [value, focused]);
  return (
    <input
      type="text"
      inputMode="decimal"
      aria-label={label}
      aria-invalid={invalid || undefined}
      className={cx('bs-exp-cell-input', kind === 'qty' ? 'is-qty' : 'is-num')}
      disabled={disabled}
      value={focused ? text : value == null ? '' : value.toLocaleString('ja-JP', { maximumFractionDigits: decimals })}
      onFocus={(e) => {
        // 0 のときは空欄にして、0 を消さずにそのまま入力できるようにする（kintone 検証指摘：燃油代）
        if (value === 0) setText('');
        setFocused(true);
        e.currentTarget.select();
      }}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        setFocused(false);
        if (value === 0 && text.trim() === '') return; // 0 の欄を空のまま離れた場合は 0 のまま
        const n = parseNumber(text);
        const v = n == null ? null : decimals ? Math.round(n * 10 ** decimals) / 10 ** decimals : Math.trunc(n);
        if (v !== value) onChange(v);
      }}
    />
  );
}

/** 表セル用の日付入力。共通の DateField（独自 DatePicker）のコンパクト版（size="sm"）を使う */
export function CellDate({
  value,
  onChange,
  disabled,
  label,
  invalid,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
  disabled?: boolean;
  label: string;
  invalid?: boolean;
}) {
  return (
    <DateField
      size="sm"
      aria-label={label}
      invalid={invalid}
      disabled={disabled}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
    />
  );
}

export function CellText({ value, onChange, label, disabled }: { value: string | null; onChange: (v: string | null) => void; label: string; disabled?: boolean }) {
  return (
    <input
      type="text"
      aria-label={label}
      className="bs-exp-cell-input is-text"
      disabled={disabled}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
    />
  );
}

/** 税算出（四捨五入／切り捨て）のトグル */
export function RoundingToggle({ value, onChange, label }: { value: '四捨五入' | '切り捨て'; onChange: (v: '四捨五入' | '切り捨て') => void; label: string }) {
  return (
    <span className="bs-exp-rounding" role="group" aria-label={label}>
      {(['四捨五入', '切り捨て'] as const).map((m) => (
        <button key={m} type="button" aria-pressed={value === m} onClick={() => onChange(m)}>
          {m === '四捨五入' ? '四捨' : '切捨'}
        </button>
      ))}
    </span>
  );
}
